<template>
  <form class="form google-connect-form">
    <div class="google-connect-form__subtitle margin-bottom-md">
      Choose a column to map new {{ type }} element to...
    </div>

    <div class="google-connect-form__controls">
      <label>
        Data Column

        <select
          v-model="columnToMap"
          data-test="graphics-sheetsMapModal-selectColumnToMap"
          data-entity="graphics-sheetsMapModal-selectColumnToMap"
        >
          <option
            v-for="(col, index) in dataColumns"
            :key="index + col"
            :value="index"
          >
            Column {{ $filters.sheetColumn(index) }} - {{ col }}
          </option>
        </select>
      </label>
    </div>

    <label>Preview</label>

    <div
      class="google-connect-form__row-preview"
      data-test="graphics-sheetsMapModal-rowPreview"
    >
      <div v-for="(row, index) in previewRows">
        <span> {{ index + 1 }}. </span>
        <span v-if="row.isImage">
          <img :src="row.text" height="30" />
        </span>
        <span v-else>
          {{ row.text }}
        </span>
      </div>
    </div>

    <div class="google-connect-form__footer flex">
      <div class="flex-grow" />

      <button
        type="submit"
        class="btn btn-style-a font-weight-bold"
        data-test="graphics-sheetsMapModal-submit"
        data-entity="graphics-sheetsMapModal-submit"
        @click.prevent="onSubmit"
      >
        Add Element
      </button>
    </div>
  </form>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  emits: ['submit', 'update:title'],
  name: 'GoogleSheetsMap',
  props: {
    // The Data Provider element that contains connection metadata
    dataProvider: {
      required: true,
    },
    element: {
      required: true,
    },
    type: {
      required: false,
      default: 'text',
    },
    title: {
      type: String,
    },
  },
  data() {
    return {
      columnToMap: 0,
      modalTitle: 'Map To Column',
    };
  },
  computed: {
    ...mapState({
      sheets(state) {
        return state[this.dataProvider.id].sheets;
      },

      forceConnect(state) {
        return state[this.dataProvider.id].forceConnect;
      },

      isLoadingData(state) {
        return state[this.dataProvider.id].isLoading;
      },

      sheetsData(state) {
        return state[this.dataProvider.id].sheetsData;
      },
    }),
    ...mapGetters('graphicsTemplate', [
      'getElementById',
    ]),
    dataColumns() {
      if (!this.sheetsData?.length) {
        return [];
      }

      // TODO: This will need to be updated if we support row indices
      const cols = Math.max(...this.sheetsData.map((r) => r.length));

      const getColumnName = (val) =>
        val ? val.text || val.img || '(Blank Header)' : '(Blank Header)';

      // Just use the first row to signify columns
      return Array.from({ length: cols }).map((v, i) =>
        getColumnName(this.sheetsData[0][i])
      );
    },
    previewRows() {
      if (!this.sheetsData?.length) {
        return [];
      }

      const idx = parseInt(this.columnToMap, 10);

      // Just slap 100 preview rows in there. It can be scrollable anyway
      // TODO: Update so we can use image sources in preview
      const rows = this.sheetsData.slice(0, 100).map((row) => {
        if (typeof row[idx]?.text !== 'undefined') {
          return {
            text: row[idx].text || '',
          };
        }

        if (typeof row[idx]?.img !== 'undefined') {
          return {
            text: row[idx]?.img,
            isImage: true,
          };
        }

        return { text: '<empty>' };
      });

      return rows;
    },
  },
  methods: {
    onSubmit() {
      const elementType = this.type;
      let mappedProperty;

      switch (elementType) {
        case 'image':
          mappedProperty = 'imageUrl';
          break;
        case 'qrcode':
          mappedProperty = 'permalink';
          break;
        default:
          mappedProperty = 'text';
          break;
      }

      const settings = {
        data:
          this.columnToMap >= 0
            ? {
              provider: this.dataProvider.id,
              dataKeyMap: {
                [mappedProperty]: this.columnToMap,
              },
            }
            : undefined,
        styles: {
          top: 0,
          left: this.element.styles.width + 10 || 0, // Slap this bad boy right at the end
          width: 300,
        },
      };

      if (this.type === 'qrcode') {
        // Square QR codes
        settings.styles.height = this.element.styles.height || 100;
        settings.styles.width = settings.styles.height || 100;
      }

      if (this.columnToMap >= 0) {
        const columnHeader = this.sheetsData[0][this.columnToMap];
        settings.name = columnHeader?.text || settings.name;
      }

      this.$emit('submit', settings);
    },
  },
  created() {
    this.$emit('update:title', this.modalTitle);
  },
};
</script>
